import { t } from '@lingui/macro'
import { useEffect, useState } from 'react'

import { mixpanel } from '@/mixpanel'
import { useToast } from '@/modules/shared/hooks/useToast'
import { useToggleWaitlist } from './useToggleWaitlist'

import type { TimelineContextType } from '@/modules/timeline/types/timeline-context.type'
import type { Appointment } from '../types/appointment.types'

/**
 * The "call me if something comes free" switch of an appointment: its optimistic state, the
 * question before withdrawing, and the toasts around the request.
 *
 * The timeline is deliberately not reloaded after a change. `forceReload` puts the whole timeline
 * into its loading state, which unmounts the detail page and remounts it with the switch back at
 * its initial value — a visible flick to off, the one thing the screen must not do. The change is
 * held over the shared list through `patchAppointment` instead, so leaving the page and coming
 * back still shows it. The SIU^S14 echo reaches the next natural load.
 */
function useAppointmentWaitlist(
  appointment: Appointment | undefined,
  patchAppointment: TimelineContextType['patchAppointment'],
) {
  const { showSuccessToast, showErrorToast } = useToast()
  const { toggleWaitlist, isLoading: isToggling } = useToggleWaitlist()
  // Rendered from local state rather than straight from `meta`, so the switch answers the tap
  // immediately. The HL7 echo from Synops is the source of truth; whenever a reload brings it in,
  // it replaces whatever the optimistic switch is showing.
  const [waitlisted, setWaitlisted] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)

  useEffect(() => {
    setWaitlisted(appointment?.meta?.waitlisted === true)
  }, [appointment?.meta?.waitlisted])

  async function apply(next: boolean) {
    if (!appointment) {
      return
    }

    const previousMeta = appointment.meta

    // Optimistic: the switch moves now and is put back if Synops refuses.
    setWaitlisted(next)
    patchAppointment(appointment.id, { meta: { ...previousMeta, waitlisted: next } })
    mixpanel.track('appointment_waitlist_toggle', { enabled: next, origin: 'detail' })

    try {
      await toggleWaitlist(appointment.id, next)
      showSuccessToast({
        message: next
          ? t({
              id: 'appointments.toast.waitlist-added',
              comment: 'Toast after asking to be called for an earlier appointment',
              message: 'We geven je vraag door aan de dienst.',
            })
          : t({
              id: 'appointments.toast.waitlist-withdrawn',
              comment: 'Toast after withdrawing the request for an earlier appointment',
              message: 'Je vraag is ingetrokken.',
            }),
      })
    } catch {
      setWaitlisted(!next)
      patchAppointment(appointment.id, { meta: previousMeta })
      showErrorToast({
        message: t({
          id: 'appointments.toast.waitlist-failed',
          comment: 'Toast when the waitlist request could not be changed',
          message: 'We konden je vraag niet doorgeven. Probeer het later opnieuw.',
        }),
      })
    }
  }

  // Asking to be called costs the patient nothing, so it applies straight away; giving the request
  // up is worth one question first.
  function requestChange() {
    if (waitlisted) {
      mixpanel.track('appointment_waitlist_withdraw_intent')
      setShowWithdrawModal(true)

      return
    }

    apply(true)
  }

  // Closed after the request rather than before it, so the modal's own loading state is the one
  // the patient sees while Synops is answering.
  async function confirmWithdraw() {
    await apply(false)
    setShowWithdrawModal(false)
  }

  function keep() {
    mixpanel.track('appointment_waitlist_withdraw_kept')
    setShowWithdrawModal(false)
  }

  return { waitlisted, isToggling, showWithdrawModal, requestChange, confirmWithdraw, keep }
}

export { useAppointmentWaitlist }
